import {
  Anchor, Badge, Button, Container, Group, Pagination, Stack,
  Table, Text, Title, Tooltip,
} from '@mantine/core';
import { IconFilterOff, IconPlus } from '@tabler/icons-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import {
  ColumnMultiSelectFilter, ColumnTextFilter, hasActiveFilters,
} from '../components/ColumnFilters';
import {
  SortableTh, nextSort, sortParam, type SortState,
} from '../components/SortableHeader';
import { StatusExplained } from '../components/StatusExplained';

// Task #239 (2026-06-02): PersonRef removed — Person column now reads
// server-resolved person_display_name from InsurancePolicyPartyOut.
// Task #362 (2026-06-09 §3f sweep): trashcan + restore removed from list rows.
// Delete/restore now live exclusively on the policy-party detail page Edit mode,
// mirroring the app-wide convention established by Task #245.
import { useInsurancePolicyPartiesList } from '../hooks/useInsurancePolicyParties';
import { PendingCRBadge } from '../components/PendingCRBadge';
import { NO_STATUS_SELECTED_HINT, StatusFilterPills } from '../components/StatusFilterPills';
import type { InsurancePolicyPartyRole } from '../api/types';
import { PARTY_ROLE_LABELS } from '../api/types';

const PAGE_SIZE = 50;
type PolicyPartySortKey = 'policy' | 'person' | 'role' | 'designation' | 'share' | 'status';

// Role-chip palette mirrors the policy / agreement detail pages so the
// same role always reads the same colour everywhere in the app.
const ROLE_COLOURS: Record<InsurancePolicyPartyRole, string> = {
  POLICY_HOLDER: 'blue',
  LIFE_INSURED: 'teal',
  BENEFICIARY: 'orange',
};

export function InsurancePolicyPartiesListPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  // #39/old-#409 (2026-06-11): two-bucket pills (policy parties are
  // soft-delete-only). Replaces the old "Include deleted" switch, whose
  // state the hook call NEVER received — a latent dead toggle since the
  // #362 refactor, fixed by this wiring.
  const [aliveSelected, setAliveSelected] = useState(true);
  const [deletedSelected, setDeletedSelected] = useState(false);
  const hasSelection = aliveSelected || deletedSelected;

  // ── #46 Wave 1 (2026-06-12): per-column predicate filters (C-132).
  const [rolesF, setRolesF] = useState<string[]>([]);
  const [policyQ, setPolicyQ] = useState('');
  const [personQ, setPersonQ] = useState('');
  const [desigF, setDesigF] = useState<string[]>([]);

  // P41 (2026-10-15): header sort, server-side across pages; null = by role.
  const [sort, setSortState] = useState<SortState<PolicyPartySortKey> | null>(null);
  function onSort(k: PolicyPartySortKey) {
    setSortState((s) => nextSort(s, k));
    setPage(1);
  }

  const filtersActive = hasActiveFilters({ rolesF, policyQ, personQ, desigF });
  function clearFilters() {
    setRolesF([]); setPolicyQ(''); setPersonQ(''); setDesigF([]);
    setPage(1);
  }
  function paged<T>(setter: (v: T) => void) {
    return (v: T) => { setter(v); setPage(1); };
  }

  const { data, isLoading, error } = useInsurancePolicyPartiesList(
    undefined, undefined, undefined,
    {
      includeDeleted: aliveSelected && deletedSelected,
      onlyDeleted: !aliveSelected && deletedSelected,
      enabled: hasSelection,
      roles: rolesF.length > 0 ? (rolesF as InsurancePolicyPartyRole[]) : undefined,
      personQ: personQ || undefined,
      policyQ: policyQ || undefined,
      designations: desigF.length > 0 ? desigF : undefined,
      // P41 + the paging fix (2026-10-15): page and sort now reach the server.
      sort: sortParam(sort),
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    },
  );
  // Task #362 (§3f sweep): del / restore / confirmDelete removed — list rows
  // no longer trigger destructive actions. See detail page top-right.
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <Container size="xl">
      <Stack>
        <Group justify="space-between">
          <Title order={2}>Insurance policy parties</Title>
          <Button leftSection={<IconPlus size={16} />} onClick={() => navigate('/insurance-policy-parties/new')}>
            New role assignment
          </Button>
        </Group>

        {/* #39/old-#409 (2026-06-11): two-bucket variant of the shared
            pills — Active = alive bucket, Deleted = deleted bucket. */}
        <StatusFilterPills
          entity="party"
          availableStatuses={['Active']}
          statuses={aliveSelected ? ['Active'] : []}
          onStatusesChange={(next) => { setAliveSelected(next.includes('Active')); setPage(1); }}
          includeDeleted={deletedSelected}
          onIncludeDeletedChange={(next) => { setDeletedSelected(next); setPage(1); }}
        />

        {error && <Text c="red">{error.detail}</Text>}

        <Table highlightOnHover withTableBorder>
          <Table.Thead>
            {/* #55 (2026-06-13, Pedro): column order Policy, Person, Role, ... */}
            <Table.Tr>
              {/* P41 (2026-10-15): every header sorts (server-side, across pages). */}
              <SortableTh label="Policy" col="policy" sort={sort} onSort={onSort} filtered={!!policyQ} />
              <SortableTh label="Person" col="person" sort={sort} onSort={onSort} filtered={!!personQ} />
              <SortableTh label="Role" col="role" sort={sort} onSort={onSort} filtered={rolesF.length > 0} w={160} />
              <SortableTh label="Designation" col="designation" sort={sort} onSort={onSort} filtered={desigF.length > 0} w={140} />
              <SortableTh label="Share %" col="share" sort={sort} onSort={onSort} w={110} />
              <SortableTh label="Status" col="status" sort={sort} onSort={onSort} w={200} />
              {/* Task #362 (2026-06-09 §3f sweep): Actions column removed —
                  delete/restore live exclusively on the detail page. */}
            </Table.Tr>
            {/* #46 Wave 1 (2026-06-12): per-column filter row (C-132).
                Share % unfiltered; Status owned by the bucket pills. */}
            <Table.Tr>
              <Table.Th>
                <ColumnTextFilter value={policyQ} onChange={paged(setPolicyQ)}
                  placeholder="Insurer / policy #…" ariaLabel="Filter by policy" />
              </Table.Th>
              <Table.Th>
                <ColumnTextFilter value={personQ} onChange={paged(setPersonQ)}
                  placeholder="Person…" ariaLabel="Filter by person" />
              </Table.Th>
              <Table.Th>
                <ColumnMultiSelectFilter values={rolesF} onChange={paged(setRolesF)}
                  options={[
                    { value: 'POLICY_HOLDER', label: 'Policy holder' },
                    { value: 'LIFE_INSURED', label: 'Life insured' },
                    { value: 'BENEFICIARY', label: 'Beneficiary' },
                  ]}
                  allLabel="All" ariaLabel="Filter by role" />
              </Table.Th>
              <Table.Th>
                <ColumnMultiSelectFilter values={desigF} onChange={paged(setDesigF)}
                  options={[
                    { value: 'PRIMARY', label: 'Primary' },
                    { value: 'CONTINGENT', label: 'Contingent' },
                  ]}
                  allLabel="All" ariaLabel="Filter by designation" />
              </Table.Th>
              <Table.Th />
              <Table.Th>
                {filtersActive && (
                  <Tooltip label="Clear all column filters" withArrow>
                    <Button size="compact-xs" variant="subtle" color="gray"
                      leftSection={<IconFilterOff size={14} />}
                      onClick={clearFilters}>
                      Clear
                    </Button>
                  </Tooltip>
                )}
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {/* #39: zero pills → hint instead of data (no fetch fired). */}
            {!hasSelection && (
              <Table.Tr><Table.Td colSpan={6}><Text c="dimmed">{NO_STATUS_SELECTED_HINT}</Text></Table.Td></Table.Tr>
            )}
            {hasSelection && isLoading && <Table.Tr><Table.Td colSpan={6}><Text c="dimmed">Loading…</Text></Table.Td></Table.Tr>}
            {/* #46 / C-132: two-tier empty state. */}
            {hasSelection && !isLoading && data?.items.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  {filtersActive ? (
                    <Group gap="xs" justify="center" py="sm">
                      <Text c="dimmed" size="sm">No policy parties match the current filters.</Text>
                      <Button size="compact-xs" variant="subtle"
                        leftSection={<IconFilterOff size={14} />}
                        onClick={clearFilters}>
                        Clear filters
                      </Button>
                    </Group>
                  ) : (
                    <Text c="dimmed">No policy parties.</Text>
                  )}
                </Table.Td>
              </Table.Tr>
            )}
            {hasSelection && data?.items.map((p) => (
              <Table.Tr key={p.id} style={{ cursor: 'pointer', opacity: p.deleted_at ? 0.5 : 1 }}
                onClick={() => navigate(`/insurance-policy-parties/${p.id}`)}>
                {/* §3h label polish (Pedro, 2026-06-10): the API has carried
                    policy_label ("Insurer · Policy#") since round-5 precisely
                    to replace UUID prefixes — this page just never rendered
                    it. Link to the policy; UUID prefix only as fallback. */}
                <Table.Td onClick={(e) => e.stopPropagation()}>
                  {p.policy_label ? (
                    <Anchor component={Link} to={`/account-insurance/${p.policy_id}`} size="sm">
                      {p.policy_label}
                    </Anchor>
                  ) : (
                    <Text size="xs" ff="monospace" c="dimmed">{p.policy_id.slice(0, 8)}…</Text>
                  )}
                </Table.Td>
                <Table.Td onClick={(e) => e.stopPropagation()}>
                  {p.person_display_name ? (
                    <Anchor component={Link} to={`/persons/${p.person_id}`} size="sm">
                      {p.person_display_name}
                    </Anchor>
                  ) : (
                    <Text size="xs" ff="monospace" c="dimmed">{p.person_id.slice(0, 8)}…</Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Badge variant="light" color={ROLE_COLOURS[p.role]}>{PARTY_ROLE_LABELS[p.role] ?? p.role}</Badge>
                </Table.Td>
                <Table.Td>
                  {p.designation ? <Badge variant="light">{p.designation}</Badge> : <Text c="dimmed">—</Text>}
                </Table.Td>
                <Table.Td>{p.percentage_share ?? '—'}</Table.Td>
                <Table.Td>
                  <Stack gap={4} align="flex-start">
                    <StatusExplained entity="party" status={p.deleted_at ? 'Deleted' : 'Active'}>
                    {p.deleted_at
                      ? <Badge color="gray" variant="light">Deleted</Badge>
                      : <Badge color="green" variant="light">Active</Badge>}
                    </StatusExplained>
                    <PendingCRBadge crs={p.pending_change_requests} />
                  </Stack>
                </Table.Td>
                {/* Task #362 (§3f sweep): per-row trashcan + restore removed.
                    Delete/Undelete live on the policy-party detail page. */}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>

        {totalPages > 1 && <Pagination value={page} onChange={setPage} total={totalPages} />}
      </Stack>
    </Container>
  );
}
